import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  Users, 
  RefreshCw, 
  Search, 
  CheckCircle, 
  XCircle, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  Mail
} from "lucide-react";

interface BrevoContact {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  doubleOptIn: boolean;
  optInDate: string | null;
  createdAt: string;
  modifiedAt: string;
  emailBlacklisted: boolean;
  listIds: number[];
}

const BrevoContactsList = () => {
  const [contacts, setContacts] = useState<BrevoContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const limit = 25;

  useEffect(() => {
    loadContacts();
  }, [offset]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.functions.invoke("brevo-contacts", {
        body: { limit, offset }
      });

      if (error) throw error;

      if (data?.success) {
        setContacts(data.contacts || []);
        setTotal(data.total || 0);
      } else {
        throw new Error(data?.error || "Erreur lors du chargement");
      }
    } catch (error: any) {
      console.error("Erreur chargement contacts Brevo:", error);
      toast.error("Erreur lors du chargement des contacts Brevo");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setOffset(0);
    loadContacts();
  };

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      contact.email.toLowerCase().includes(search) ||
      contact.firstName?.toLowerCase().includes(search) ||
      contact.lastName?.toLowerCase().includes(search)
    );
  });

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Contacts Brevo
            </CardTitle>
            <CardDescription>
              Liste des contacts synchronisés avec Brevo ({total} contacts)
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par email ou nom..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Aucun contact Brevo</p>
            <p className="text-sm">Les contacts inscrits à la newsletter apparaîtront ici</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Prénom</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Double Opt-in</TableHead>
                    <TableHead>Date inscription</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          {contact.email}
                        </div>
                      </TableCell>
                      <TableCell>{contact.firstName || "-"}</TableCell>
                      <TableCell>{contact.lastName || "-"}</TableCell>
                      <TableCell>
                        {contact.doubleOptIn ? (
                          <Badge className="bg-green-500">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirmé
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            En attente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.createdAt 
                          ? format(parseISO(contact.createdAt), "d MMM yyyy", { locale: fr })
                          : "-"
                        }
                      </TableCell>
                      <TableCell>
                        {contact.emailBlacklisted ? (
                          <Badge variant="destructive">
                            <XCircle className="w-3 h-3 mr-1" />
                            Bloqué
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-green-500 text-green-600">
                            Actif
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden space-y-3">
              {filteredContacts.map((contact) => (
                <Card key={contact.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm font-medium truncate flex-1">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{contact.email}</span>
                    </div>
                    {contact.emailBlacklisted ? (
                      <Badge variant="destructive" className="text-[10px] ml-2">
                        Bloqué
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-green-500 text-green-600 text-[10px] ml-2">
                        Actif
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    {contact.firstName || contact.lastName 
                      ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim()
                      : "Nom non renseigné"
                    }
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {contact.createdAt 
                        ? format(parseISO(contact.createdAt), "d MMM yyyy", { locale: fr })
                        : "-"
                      }
                    </span>
                    {contact.doubleOptIn ? (
                      <Badge className="bg-green-500 text-[10px]">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Confirmé
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">
                        En attente
                      </Badge>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} sur {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={offset === 0 || loading}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={offset + limit >= total || loading}
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BrevoContactsList;
